// src/features/auth/route-protection/hooks/usePermissions.ts
import { useCallback, useMemo } from 'react';
import { useAppSelector } from '@/app/hooks';
import { hasPermission, hasAnyPermission, hasAllPermissions } from '@/features/auth/utils/permission.utils';

interface UsePermissionsResult {
  permissions: string[];
  hasPermission: (permission: string) => boolean;
  hasAnyPermission: (permissions: string[]) => boolean;
  hasAllPermissions: (permissions: string[]) => boolean;
  checkPermission: (permission: string, throwError?: boolean) => boolean;
  isLoading: boolean;
}

/**
 * Hook for managing user permissions
 */
export function usePermissions(): UsePermissionsResult {
  const { permissions, isLoading } = useAppSelector(state => state.auth);

  // Check single permission
  const checkSinglePermission = useCallback(
    (permission: string): boolean => {
      return hasPermission(permissions, permission);
    },
    [permissions]
  );

  // Check any permission from list
  const checkAnyPermission = useCallback(
    (permissionList: string[]): boolean => {
      return hasAnyPermission(permissions, permissionList);
    },
    [permissions]
  );

  // Check all permissions from list
  const checkAllPermissions = useCallback(
    (permissionList: string[]): boolean => {
      return hasAllPermissions(permissions, permissionList);
    },
    [permissions]
  );

  // Check permission with optional error throwing
  const checkPermission = useCallback(
    (permission: string, throwError = false): boolean => {
      const hasAccess = checkSinglePermission(permission);
      
      if (!hasAccess && throwError) {
        throw new Error(`Missing required permission: ${permission}`);
      }
      
      return hasAccess;
    },
    [checkSinglePermission]
  );

  return {
    permissions,
    hasPermission: checkSinglePermission,
    hasAnyPermission: checkAnyPermission,
    hasAllPermissions: checkAllPermissions,
    checkPermission,
    isLoading,
  };
}

/**
 * Hook for checking specific permission requirements
 */
export function useRequiredPermissions(
  requiredPermissions: string[],
  requireAll = true
): {
  hasAccess: boolean;
  missingPermissions: string[];
  isLoading: boolean;
} {
  const { permissions, isLoading } = useAppSelector(state => state.auth);

  const result = useMemo(() => {
    if (requireAll) {
      const missingPermissions = requiredPermissions.filter(
        permission => !hasPermission(permissions, permission)
      );
      return {
        hasAccess: missingPermissions.length === 0,
        missingPermissions,
      };
    } else {
      const hasAccess = hasAnyPermission(permissions, requiredPermissions);
      const missingPermissions = hasAccess
        ? []
        : requiredPermissions; // All are missing if none match
      return {
        hasAccess,
        missingPermissions,
      };
    }
  }, [permissions, requiredPermissions, requireAll]);

  return {
    ...result,
    isLoading,
  };
}

/**
 * Hook for resource-specific permissions
 */
export function useResourcePermissions(
  resource: string
): {
  canView: boolean;
  canCreate: boolean;
  canUpdate: boolean;
  canDelete: boolean;
  canManage: boolean;
  isLoading: boolean;
} {
  const { permissions, isLoading } = useAppSelector(state => state.auth);

  const resourcePermissions = useMemo(() => ({
    canView: hasPermission(permissions, `${resource}:view`),
    canCreate: hasPermission(permissions, `${resource}:create`),
    canUpdate: hasPermission(permissions, `${resource}:update`),
    canDelete: hasPermission(permissions, `${resource}:delete`),
    canManage: hasPermission(permissions, `${resource}:manage`),
  }), [permissions, resource]);

  return {
    ...resourcePermissions,
    isLoading,
  };
}

/**
 * Hook for module-level permissions
 */
export function useModulePermissions(
  module: string
): {
  hasAccess: boolean;
  permissions: {
    view: boolean;
    create: boolean;
    update: boolean;
    delete: boolean;
    export: boolean;
    import: boolean;
    manage: boolean;
  };
  isLoading: boolean;
} {
  const { permissions, isLoading } = useAppSelector(state => state.auth);

  const modulePermissions = useMemo(() => {
    const perms = {
      view: hasPermission(permissions, `${module}:view`),
      create: hasPermission(permissions, `${module}:create`),
      update: hasPermission(permissions, `${module}:update`),
      delete: hasPermission(permissions, `${module}:delete`),
      export: hasPermission(permissions, `${module}:export`),
      import: hasPermission(permissions, `${module}:import`),
      manage: hasPermission(permissions, `${module}:manage`),
    };

    // Has access if they can at least view
    const hasAccess = perms.view || perms.manage;

    return {
      hasAccess,
      permissions: perms,
    };
  }, [permissions, module]);

  return {
    ...modulePermissions,
    isLoading,
  };
}